import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, X, Check } from "lucide-react";
import { toast } from "sonner";
import { auth, storage } from "../lib/supabase";

const AvatarUpload = ({
  currentAvatar,
  onImageUpload,
  avatarType,
  avatarColor,
}) => {
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor selecciona una imagen válida");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 5MB");
      return;
    }

    // Store the file for upload
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      // Get current user
      const { user, error: userError } = await auth.getUser();

      if (userError || !user) {
        toast.error("Error al obtener usuario");
        return;
      }

      // Upload to Supabase Storage
      const { data: avatarUrl, error: uploadError } = await storage.uploadAvatar(
        user.id,
        selectedFile
      );

      if (uploadError) {
        throw uploadError;
      }

      // Notify parent component with the avatar URL
      if (onImageUpload) {
        onImageUpload(avatarUrl);
      }

      toast.success("Avatar actualizado exitosamente");
      setPreview(null);
      setSelectedFile(null);
    } catch (error) {
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      // Get current user
      const { user, error: userError } = await auth.getUser();

      if (userError || !user) {
        toast.error("Error al obtener usuario");
        return;
      }

      // Delete from Supabase Storage (try common extensions)
      const extensions = ['png', 'jpg', 'jpeg', 'webp'];
      for (const ext of extensions) {
        await storage.deleteAvatar(user.id, ext);
      }

      setPreview(null);
      setSelectedFile(null);
      if (onImageUpload) {
        onImageUpload(null);
      }
      toast.info("Avatar restablecido al predeterminado");
    } catch (error) {
      toast.error("Error al eliminar el avatar");
    }
  };

  const getDefaultAvatarUrl = () => {
    // Use the app's default avatar
    return "/avatar.png";
  };

  return (
    <div className="space-y-4" data-testid="avatar-upload">
      <div className="flex items-center gap-6">
        {/* Current Avatar Preview */}
        <div className="relative">
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary/30 shadow-neon bg-background-paper">
            {preview ? (
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            ) : currentAvatar ? (
              <img
                src={currentAvatar}
                alt="Current Avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={getDefaultAvatarUrl()}
                alt="Default Avatar"
                className="w-full h-full object-contain p-2"
              />
            )}
          </div>
          {(preview || currentAvatar) && (
            <button
              onClick={handleRemove}
              className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-error text-white flex items-center justify-center hover:scale-110 transition-transform"
              data-testid="remove-avatar-btn"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Upload Button */}
        <div className="flex-1 space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="glass px-6 py-3 rounded-lg hover:border-primary/50 transition-all flex items-center gap-2 w-full justify-center"
            data-testid="select-image-btn"
          >
            <Upload className="w-5 h-5 text-primary" />
            <span className="text-text-primary font-medium">
              Seleccionar Imagen
            </span>
          </button>

          {preview && (
            <motion.button
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={handleUpload}
              disabled={uploading}
              className="btn-neon px-6 py-3 rounded-lg flex items-center gap-2 w-full justify-center disabled:opacity-50"
              data-testid="upload-btn"
            >
              <Check className="w-5 h-5" />
              <span>{uploading ? "Subiendo..." : "Guardar Avatar"}</span>
            </motion.button>
          )}
        </div>
      </div>

      <p className="text-xs text-text-muted">
        Recomendado: Imagen cuadrada de al menos 200x200px. Máximo 5MB.
      </p>
    </div>
  );
};

export default AvatarUpload;
